import { FC, useCallback, useMemo, useState } from 'react';
import { DeviceToken, User } from '../../../types/User';
import { DefaultFooterComponent, Icons, showAlert, Table, TableProps } from '@silverfort/sf-components';
import { TOKEN_TABLE_COLUMNS } from '../consts';
import { TokenRow } from '../types';
import { useDeleteToken, UseDeleteTokenParams } from '../../../api';
import { useSnackAlert } from '../../../context/SnackAlertProvider';
import { usePopup } from '../../../context/PopupProvider';
import { useTestTokensPopup } from '../hooks/useTestTokensPopup';
import { useBlinkRows } from '../../../hooks/useBlinkRows';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { UserSession } from '../../../../../api';

export interface TokensTableProps {
    tokens: User['tokens'];
    tokenToFocus?: DeviceToken['id'];
    onTokenRemoved: (tokenId: DeviceToken['id']) => void;
    userSession: UserSession;
}

const ROW_HEIGHT = 48;

const tokenKeyExtractor = (token: TokenRow) => token.id;

const convertTokensToRows = (tokens: User['tokens']): TokenRow[] => {
    return tokens.map((token) => ({
        idDisplay: token.id.substring(token.id.length - 8),
        id: token.id,
        type: token.type,
        name: token.machineName,
        pairedAt: new Date(token.createDate),
        lastUsed: token.lastUsed,
        userId: token.userId
    }));
};

const TokensTable: FC<TokensTableProps> = ({ tokens, tokenToFocus, onTokenRemoved, userSession }) => {
    const { openPopup } = usePopup();
    const { openSnackAlert, closeSnackAlert } = useSnackAlert();
    const [tokensToTest, setTokenToTest] = useState<DeviceToken[]>([]);
    const rows = convertTokensToRows(tokens);
    const tokensToBlink = useMemo(() => (tokenToFocus ? [tokenToFocus] : []), [tokenToFocus]);
    const { highlightedRows } = useBlinkRows(tokensToBlink);
    const handleDeleteTokenSuccess: UseDeleteTokenParams['onSuccess'] = useCallback(
        ({ tokenId }) => {
            openSnackAlert({
                message: `The token ${tokenId.substring(tokenId.length - 8)} was deleted`,
                iconLeft: Icons.Block,
                onClose: closeSnackAlert
            });

            onTokenRemoved(tokenId);
        },
        [openSnackAlert, closeSnackAlert, onTokenRemoved]
    );

    const handleDeleteTokenError = useCallback(() => {
        showAlert('Failed to delete token', 'error');
    }, []);

    const { openTestTokensPopup } = useTestTokensPopup({ tokens: tokensToTest });

    const { deleteToken } = useDeleteToken({
        onSuccess: handleDeleteTokenSuccess,
        onError: handleDeleteTokenError
    });

    const handleDeleteToken = (tokenRow?: TokenRow) => {
        if (tokenRow) {
            openPopup({
                title: 'Delete token?',
                ContentComponent: () => <span className="font-sf">Are you sure you want to delete this token?</span>,
                FooterComponent: DefaultFooterComponent,
                onApprove: () => deleteToken(tokenRow.userId, tokenRow.id),
                FooterComponentProps: {
                    primaryButtonProps: { text: 'Delete' }
                }
            });
        }
    };

    const handleTestToken = (tokenRow?: TokenRow) => {
        const token = tokens.find((t) => t.id === tokenRow?.id);
        if (token) {
            setTokenToTest([token]);
            openTestTokensPopup();
        }
    };

    const menuItems: TableProps<TokenRow, TokenRow['id']>['menuItems'] = [
        {
            title: 'Test',
            disabled: !isUserPermitted(userSession, 'testUserTokens', ROLES.EDITOR.value),
            onClick: handleTestToken
        },
        {
            title: 'Delete',
            disabled: !isUserPermitted(userSession, 'deleteUserTokens', ROLES.EDITOR.value),
            onClick: handleDeleteToken
        }
    ];

    // todo: fix the props types sf-components - https://silverfort.atlassian.net/browse/AF-36668
    return (
        <Table
            // todo: remove the font after - https://silverfort.atlassian.net/browse/AF-36668
            tableContainerClassName="font-sf"
            columns={TOKEN_TABLE_COLUMNS}
            rows={rows}
            keyExtractor={tokenKeyExtractor}
            bodyHeight={Math.min(ROW_HEIGHT * rows.length, 200)}
            selectable={false}
            menuItems={menuItems}
            highlighted={highlightedRows}
            EmptyStateProps={{
                title: 'No tokens yet',
                className: '[&&]:h-50'
            }}
        />
    );
};

export default TokensTable;
